import { useMutation, useQueryClient } from "@tanstack/react-query";
import ApiHttp from "byzantine/src/ApiHttp";

import { TRANSACTION_DETAIL_QUERY_KEY } from "../constants";

import { TRANSACTIONS_LIST_QUERY_KEY } from "./useTransactionsList";

import type { TransactionsListResponse } from "./useTransactionsList";

export interface UpdateNoteInput {
  transactionId: string;
  note: string;
}

const applyNote = (
  response: TransactionsListResponse | undefined,
  transactionId: string,
  note: string
): TransactionsListResponse | undefined => {
  if (!response) return response;
  return {
    ...response,
    transactions: response.transactions.map((transaction) =>
      transaction.id === transactionId ? { ...transaction, note } : transaction
    ),
  };
};

const restoreNote = (
  response: TransactionsListResponse | undefined,
  previous: API.Transaction,
  optimisticNote: string
): TransactionsListResponse | undefined => {
  if (!response) return response;
  return {
    ...response,
    transactions: response.transactions.map((transaction) =>
      transaction.id === previous.id && transaction.note === optimisticNote
        ? { ...transaction, note: previous.note }
        : transaction
    ),
  };
};

/**
 * Callers handle failure themselves, through `mutateAsync`, because routing it needs to know
 * whether the row is still on screen.
 */
export const useUpdateTransactionNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ transactionId, note }: UpdateNoteInput) =>
      ApiHttp.fetch(
        `transactions/${transactionId}`,
        { method: "PUT" },
        { note }
      ),

    onMutate: async ({ transactionId, note }: UpdateNoteInput) => {
      await queryClient.cancelQueries({
        queryKey: [TRANSACTIONS_LIST_QUERY_KEY],
      });
      await queryClient.cancelQueries({
        queryKey: [TRANSACTION_DETAIL_QUERY_KEY, transactionId],
      });

      const previousRows = queryClient
        .getQueriesData<TransactionsListResponse>({
          queryKey: [TRANSACTIONS_LIST_QUERY_KEY],
        })
        .flatMap(([key, response]) => {
          const row = response?.transactions.find(
            (transaction) => transaction.id === transactionId
          );
          return row ? [[key, row] as const] : [];
        });

      queryClient.setQueriesData<TransactionsListResponse>(
        { queryKey: [TRANSACTIONS_LIST_QUERY_KEY] },
        (response) => applyNote(response, transactionId, note)
      );

      return { previousRows };
    },

    onError: (_error, { note }: UpdateNoteInput, context) => {
      context?.previousRows.forEach(([key, row]) => {
        queryClient.setQueryData<TransactionsListResponse>(key, (response) =>
          restoreNote(response, row, note)
        );
      });
    },

    // Query-managed, not a side fetch: cancelQueries above cancels a stale one from an earlier
    // settle, and returning the promise keeps isPending true until the panel is actually fresh.
    onSettled: (_data, _error, { transactionId }: UpdateNoteInput) =>
      queryClient.invalidateQueries({
        queryKey: [TRANSACTION_DETAIL_QUERY_KEY, transactionId],
      }),
  });
};
